import React, { useState, useEffect } from 'react';
import { customBaseUrl, httpGet } from '../../store/http';
import { NotificationManager } from 'react-notifications';
import { Input, Button, Form, Row, Col, Card } from 'antd';
import { DataGrid } from '@mui/x-data-grid';
import { Box } from '@mui/material';
import AdminLayout from "../../components/Layout";
import LoanDashboardLayout from '../../components/Layout/LoanDashboardLayout';
import { showLoader, hideLoader } from '../../utils/loader';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import slugs from '../../resources/slugs';

const LoansByStatusPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { from } = location.state || {};
  const { status: statusParam } = useParams(); // Get status from URL parameter
  const { initialStatus } = location.state || {}; // Fallback to state if not in URL

  const currentStatus = statusParam || initialStatus; // Prioritize URL param

  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 0, limit: 10 });
  const [totalRows, setTotalRows] = useState(0);

  const fetchLoans = async (currentPage, currentPageSize) => {
    setLoading(true);
    showLoader();
    try {
      const token = from === 'admin' ? localStorage.getItem('token') : localStorage.getItem('loanAdminToken');
      if (!token) {
        window.location.href = '/loan-admin-login';
        return;
      }

      const queryParams = new URLSearchParams({
        page: currentPage + 1, // API is 1-based
        limit: currentPageSize,
        status: currentStatus, // Use the resolved status
      });

      const res = await httpGet(
        `${customBaseUrl.loanUrl}/api/v1/admin/loans-by-status?${queryParams.toString()}`,
        null, false, false, token
      );

      if (res.status && res.data) {
        setLoans(res.data || []);
        setTotalRows(res.pagination.total || 0);
      } else {
        NotificationManager.error(res.message || 'Failed to fetch loans');
      }
    } catch (error) {
      console.error('Error fetching loans:', error);
      NotificationManager.error('Error fetching loans');
    } finally {
      setLoading(false);
      hideLoader();
    }
  };

  useEffect(() => {
    if (currentStatus) { // Fetch only if a status is provided
      fetchLoans(pagination.page, pagination.limit);
    } else {
      setLoading(false);
      hideLoader();
    }
  }, [pagination.page, pagination.limit, currentStatus]);

  const columns = [
    { field: 'loan_id', headerName: 'Loan ID', width: 250 },
    {
      field: 'name',
      headerName: 'Name',
      width: 180,
      valueGetter: (params) => `${params.row.first_name} ${params.row.last_name}`,
    },
    { field: 'email', headerName: 'Email', width: 200 },
    { field: 'phone', headerName: 'Phone', width: 150 },
    { field: 'type', headerName: 'Type', width: 100 },
    { field: 'amount', headerName: 'Amount', width: 120 },
    { field: 'status', headerName: 'Status', width: 180 },
    {
      field: 'created_at',
      headerName: 'Created At',
      width: 200,
      valueFormatter: ({ value }) => new Date(value).toLocaleString(),
    },
    {
      field: 'action',
      headerName: 'Action',
      width: 150,
      renderCell: (params) => {
        const viewSlug = from === 'admin' ? slugs.loan_admin_view : slugs.loan_view;
        return (
          <Button type="link" onClick={() => navigate(viewSlug.replace(':id', params.row.loan_id), { state: { from } })}>
            View Details
          </Button>
        );
      },
    },
  ];

  const Layout = from === "admin" ? AdminLayout : LoanDashboardLayout;

  return (
    <Layout>
      <h1>Loans By Status: {currentStatus}</h1>
      <Box sx={{ height: 650, width: '100%' }}>
        <DataGrid
          getRowId={(row) => row.loan_id}
          rows={loans}
          columns={columns}
          loading={loading}
          pagination
          paginationMode="server"
          rowCount={totalRows}
          pageSize={pagination.limit}
          page={pagination.page}
          onPageChange={(newPage) => setPagination(prev => ({ ...prev, page: newPage }))}
          onPageSizeChange={(newPageSize) => setPagination({ page: 0, limit: newPageSize })}
          rowsPerPageOptions={[10, 20, 50]}
        />
      </Box>
    </Layout>
  );
};

export default LoansByStatusPage;
